import { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabase } from '../../../lib/supabase';

export interface NotificationOptInRef {
  expand: () => void;
}

interface FormData {
  email: string;
  phone: string;
  make: string;
  model: string;
  bodyType: string;
  minPrice: string;
  maxPrice: string;
  alertTypes: {
    priceDrops: boolean;
    justArrived: boolean;
    lastChance: boolean;
    almostSold: boolean;
  };
}

// Simulates a live "spots claimed" counter that slowly ticks up
function useUrgencyStats() {
  const [spotsClaimed, setSpotsClaimed] = useState(47);
  const [watching, setWatching] = useState(12);

  useEffect(() => {
    const id = setInterval(() => {
      setSpotsClaimed(prev => Math.min(prev + (Math.random() > 0.75 ? 1 : 0), 99));
      setWatching(prev => {
        const delta = Math.floor(Math.random() * 3) - 1;
        return Math.max(8, Math.min(prev + delta, 24));
      });
    }, 15000);
    return () => clearInterval(id);
  }, []);

  return { spotsClaimed, watching };
}

const NotificationOptIn = forwardRef<NotificationOptInRef>((props, ref) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    phone: '',
    make: '',
    model: '',
    bodyType: '',
    minPrice: '',
    maxPrice: '',
    alertTypes: {
      priceDrops: true,
      justArrived: true,
      lastChance: false,
      almostSold: false,
    },
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const successHeadingRef = useRef<HTMLHeadingElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [submittedCriteria, setSubmittedCriteria] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { spotsClaimed, watching } = useUrgencyStats();

  useImperativeHandle(ref, () => ({
    expand: () => {
      setIsExpanded(true);
      setTimeout(() => {
        document.getElementById('notification-opt-in')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    },
  }));

  // Move focus to success heading when success state renders
  useEffect(() => {
    if (isSubmitted) {
      requestAnimationFrame(() => successHeadingRef.current?.focus());
    }
  }, [isSubmitted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const criteria: string[] = [];
    if (formData.make) criteria.push(formData.make);
    if (formData.model) criteria.push(formData.model);
    if (formData.bodyType) criteria.push(formData.bodyType);
    if (formData.minPrice || formData.maxPrice) {
      const priceRange = `${formData.minPrice ? `$${formData.minPrice}` : 'any'}–${formData.maxPrice ? `$${formData.maxPrice}` : 'any'}`;
      criteria.push(priceRange);
    }

    const criteriaText = criteria.length > 0
      ? criteria.join(' ')
      : 'any vehicle that matches your preferences';

    try {
      const supabase = await getSupabase();

      const alertTypesArray: string[] = [];
      if (formData.alertTypes.priceDrops) alertTypesArray.push('price_drop');
      if (formData.alertTypes.justArrived) alertTypesArray.push('new_inventory');
      if (formData.alertTypes.lastChance) alertTypesArray.push('last_chance');
      if (formData.alertTypes.almostSold) alertTypesArray.push('matching_vehicle');

      const subscriberData = {
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        make: formData.make.trim() || null,
        model: formData.model.trim() || null,
        body_type: formData.bodyType || null,
        min_price: formData.minPrice ? parseInt(formData.minPrice, 10) : null,
        max_price: formData.maxPrice ? parseInt(formData.maxPrice, 10) : null,
        alert_types: formData.alertTypes,
        is_active: true,
      };

      const { error: insertError } = await supabase
        .from('deal_radar_subscribers')
        .insert([subscriberData]);

      if (insertError) {
        console.error('Supabase insert error:', insertError);
        setError('Our radar blipped. Try again?');
        setIsLoading(false);
        return;
      }

      const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      fetch(`${SUPABASE_URL}/functions/v1/deal-radar-subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email.trim(),
          phone: formData.phone.trim() || undefined,
          make: formData.make.trim() || undefined,
          model: formData.model.trim() || undefined,
          body_type: formData.bodyType || undefined,
          min_price: formData.minPrice ? parseInt(formData.minPrice, 10) : undefined,
          max_price: formData.maxPrice ? parseInt(formData.maxPrice, 10) : undefined,
          alert_types: alertTypesArray,
        }),
      }).catch(err => {
        console.warn('Team notification failed (non-critical):', err);
      });

      // Build URL params for thank-you page
      const params = new URLSearchParams();
      if (formData.make) params.set('make', formData.make);
      if (formData.model) params.set('model', formData.model);
      if (formData.bodyType) params.set('bodyType', formData.bodyType);
      if (formData.maxPrice) params.set('maxPrice', formData.maxPrice);
      
      // Pass selected alert types as comma-separated string
      const selectedAlerts = Object.entries(formData.alertTypes)
        .filter(([_, isSelected]) => isSelected)
        .map(([key]) => key);
      if (selectedAlerts.length > 0) {
        params.set('alerts', selectedAlerts.join(','));
      }

      // Navigate to thank-you page with criteria
      navigate(`/thank-you/deal-radar?${params.toString()}`);
    } catch (err) {
      console.error('Deal Radar subscription error:', err);
      setError('Connection hiccup. Check your internet and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckboxChange = (alertType: keyof FormData['alertTypes']) => {
    setFormData(prev => ({
      ...prev,
      alertTypes: { ...prev.alertTypes, [alertType]: !prev.alertTypes[alertType] },
    }));
  };

  // ── Urgency bar — shown in both collapsed and expanded states ──
  const UrgencyBar = () => (
    <div className="flex flex-wrap items-center gap-3 mb-5 px-1">
      <div className="flex items-center gap-2 bg-[#E8B84B]/10 border border-[#E8B84B]/25 rounded-full px-3.5 py-1.5">
        <span className="w-1.5 h-1.5 bg-[#E8B84B] rounded-full animate-pulse flex-shrink-0"></span>
        <span className="text-xs font-semibold text-[#E8B84B]">
          {spotsClaimed} spots claimed this week
        </span>
      </div>
      <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3.5 py-1.5">
        <i className="ri-eye-line text-white/75 text-xs"></i>
        <span className="text-xs text-white/75">
          <strong className="text-white/80">{watching}</strong> people setting up alerts right now
        </span>
      </div>
    </div>
  );

  if (isSubmitted) {
    return (
      <div
        id="notification-opt-in"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="bg-[#1C2B1E] rounded-xl shadow-lg p-8 mb-8"
      >
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-[#E8B84B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-line text-5xl text-[#E8B84B]"></i>
          </div>
          <h3
            ref={successHeadingRef}
            tabIndex={-1}
            className="text-3xl font-bold text-white mb-3 focus:outline-none"
          >
            You've Got First Dibs. 🎯
          </h3>
          <p className="text-lg text-white/80 mb-2">
            We'll alert you the moment <strong className="text-[#E8B84B]">{submittedCriteria}</strong> lands.
          </p>
          <p className="text-sm text-white/75 mb-6">
            Check your inbox (and maybe spam folder) for a confirmation. You can update preferences anytime.
          </p>
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-[#E8B84B]/30 rounded-full px-6 py-3 mt-4">
            <div className="w-2 h-2 bg-[#E8B84B] rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">Active — Monitoring Inventory</span>
          </div>
        </div>
      </div>
    );
  }

  if (!isExpanded) {
    return (
      <div id="notification-opt-in" className="bg-[#1C2B1E] rounded-xl shadow-lg p-6 mb-8">
        <UrgencyBar />
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex-shrink-0 bg-[#E8B84B]/20 rounded-full flex items-center justify-center">
              <i className="ri-vip-crown-line text-2xl text-[#E8B84B]"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-0.5">Don't Be the Person Who Waited Too Long.</h3>
              <p className="text-white/60 text-sm">Get alerts before the car goes live. Free. No spam. Unsubscribe anytime.</p>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(true)}
            aria-expanded={false}
            aria-controls="notification-opt-in-form"
            aria-label="Claim my First Dibs spot — expand alert sign-up form"
            className="bg-[#E8B84B] text-[#1C2B1E] hover:bg-[#d4a43a] font-bold px-6 py-3 rounded-lg transition-colors duration-200 whitespace-nowrap text-sm cursor-pointer"
          >
            Claim My First Dibs Spot
          </button>
        </div>
      </div>
    );
  }

  return (
    <div id="notification-opt-in" className="bg-[#1C2B1E] rounded-xl shadow-lg p-8 mb-8">
      <UrgencyBar />

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 flex-shrink-0 bg-[#E8B84B]/20 rounded-full flex items-center justify-center">
            <i className="ri-vip-crown-line text-2xl text-[#E8B84B]"></i>
          </div>
          <div>
            <h3 className="text-xl font-bold text-white mb-0.5">Set Up Your First Dibs</h3>
            <p className="text-white/60 text-sm">Tell us what you want. We'll watch the lot so you don't have to.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded(false)}
          aria-expanded={true}
          aria-controls="notification-opt-in-form"
          aria-label="Collapse alert sign-up form"
          className="text-white/70 hover:text-white/80 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-2xl" aria-hidden="true"></i>
        </button>
      </div>

      <form id="notification-opt-in-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            className="bg-red-500/10 border-2 border-red-500/30 rounded-lg p-4 flex items-start gap-3"
          >
            <i className="ri-error-warning-line text-xl text-red-400 flex-shrink-0 mt-0.5" aria-hidden="true"></i>
            <div>
              <p className="text-sm font-semibold text-red-300 mb-1">Oops!</p>
              <p className="text-sm text-red-200/80">{error}</p>
            </div>
          </div>
        )}

        {/* Contact Info */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="opt-in-email" className="block text-sm font-medium text-white/80 mb-2">
              Email Address <span className="text-[#E8B84B]">*</span>
            </label>
            <input
              id="opt-in-email"
              type="email"
              name="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="your@email.com"
              className="w-full px-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm"
              required
              aria-required="true"
              disabled={isLoading}
            />
          </div>
          <div>
            <label htmlFor="opt-in-phone" className="block text-sm font-medium text-white/80 mb-2">
              Phone <span className="text-white/65 font-normal">(optional — for texts)</span>
            </label>
            <input
              id="opt-in-phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="(503) 555-0100"
              className="w-full px-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm"
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Vehicle Preferences */}
        <div className="border-t border-white/10 pt-6">
          <h4 className="text-base font-bold text-white mb-4 flex items-center gap-2">
            <span className="text-sm flex-shrink-0" aria-hidden="true"><i className="ri-car-line"></i></span>
            What's Your Dream Car?
          </h4>
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div>
              <label htmlFor="opt-in-make" className="block text-sm font-medium text-white/80 mb-2">Make <span className="text-white/65 font-normal">(optional)</span></label>
              <input
                id="opt-in-make"
                type="text"
                value={formData.make}
                onChange={(e) => setFormData(prev => ({ ...prev, make: e.target.value }))}
                placeholder="e.g., Toyota"
                className="w-full px-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm"
                disabled={isLoading}
              />
            </div>
            <div>
              <label htmlFor="opt-in-model" className="block text-sm font-medium text-white/80 mb-2">Model <span className="text-white/65 font-normal">(optional)</span></label>
              <input
                id="opt-in-model"
                type="text"
                value={formData.model}
                onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                placeholder="e.g., Tacoma"
                className="w-full px-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm"
                disabled={isLoading}
              />
            </div>
            <div>
              <label htmlFor="opt-in-bodyType" className="block text-sm font-medium text-white/80 mb-2">Body Type <span className="text-white/65 font-normal">(optional)</span></label>
              <select
                id="opt-in-bodyType"
                value={formData.bodyType}
                onChange={(e) => setFormData(prev => ({ ...prev, bodyType: e.target.value }))}
                className="w-full px-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm cursor-pointer"
                disabled={isLoading}
              >
                <option value="">Any Type</option>
                <option value="Sedan">Sedan</option>
                <option value="SUV">SUV</option>
                <option value="Truck">Truck</option>
                <option value="Coupe">Coupe</option>
                <option value="Hatchback">Hatchback</option>
                <option value="Wagon">Wagon</option>
                <option value="Van">Van</option>
              </select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="opt-in-minPrice" className="block text-sm font-medium text-white/80 mb-2">Min Price <span className="text-white/65 font-normal">(optional)</span></label>
              <input
                id="opt-in-minPrice"
                type="number"
                value={formData.minPrice}
                onChange={(e) => setFormData(prev => ({ ...prev, minPrice: e.target.value }))}
                placeholder="e.g., 10000"
                className="w-full px-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm"
                disabled={isLoading}
              />
            </div>
            <div>
              <label htmlFor="opt-in-maxPrice" className="block text-sm font-medium text-white/80 mb-2">Max Price <span className="text-white/65 font-normal">(optional)</span></label>
              <input
                id="opt-in-maxPrice"
                type="number"
                value={formData.maxPrice}
                onChange={(e) => setFormData(prev => ({ ...prev, maxPrice: e.target.value }))}
                placeholder="e.g., 30000"
                className="w-full px-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        {/* Alert Types */}
        <div className="border-t border-white/10 pt-6">
          <h4
            id="alert-types-legend"
            className="text-base font-bold text-white mb-4 flex items-center gap-2"
          >
            <span className="text-sm flex-shrink-0" aria-hidden="true"><i className="ri-notification-line"></i></span>
            What Should We Hunt For?
          </h4>
          <p id="alert-types-hint" className="sr-only">
            Select one or more alert types. At least one is recommended so we know what to watch for.
          </p>
          <div
            role="group"
            aria-labelledby="alert-types-legend"
            aria-describedby="alert-types-hint"
            className="grid md:grid-cols-2 gap-3"
          >
            {[
              { key: 'priceDrops' as const, label: 'Price Drops', sub: 'When a matching vehicle\'s price gets slashed' },
              { key: 'justArrived' as const, label: 'Just Arrived', sub: 'Fresh inventory — before it\'s even photographed' },
              { key: 'lastChance' as const, label: 'Last Chance', sub: 'Priced to move fast (30+ days on lot)' },
              { key: 'almostSold' as const, label: 'High Interest', sub: 'Lots of eyes on it — act before someone else does' },
            ].map(({ key, label, sub }) => (
              <label key={key} className="flex items-start gap-3 p-4 rounded-lg border-2 border-white/10 bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  id={`alert-type-${key}`}
                  checked={formData.alertTypes[key]}
                  onChange={() => handleCheckboxChange(key)}
                  aria-describedby={`alert-type-${key}-desc`}
                  className="mt-0.5 w-5 h-5 rounded border-2 border-white/20 bg-white/10 text-[#E8B84B] focus:ring-2 focus:ring-[#E8B84B]/40 cursor-pointer"
                  disabled={isLoading}
                />
                <div>
                  <div className="text-sm font-semibold text-white">{label}</div>
                  <div id={`alert-type-${key}-desc`} className="text-xs text-white/75">{sub}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Terms */}
        <div className="flex items-start gap-3 pt-2">
          <input type="checkbox" id="terms" required aria-required="true" className="mt-1 w-4 h-4 rounded border-2 border-white/20 bg-white/10 text-[#E8B84B] focus:ring-2 focus:ring-[#E8B84B]/40 cursor-pointer" disabled={isLoading} />
          <label htmlFor="terms" className="text-sm text-white/75 cursor-pointer leading-relaxed">
            I want First Dibs alerts. I get it — this isn't a newsletter. Rockhound only reaches out when there's something worth seeing. Unsubscribe anytime.
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-[#E8B84B] text-[#1C2B1E] hover:bg-[#d4a43a] font-bold py-4 rounded-lg transition-colors duration-200 whitespace-nowrap text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C2B1E]"
        >
          {isLoading ? (
            <><i className="ri-loader-4-line text-xl animate-spin"></i><span>Locking You In...</span></>
          ) : (
            <><i className="ri-vip-crown-line text-lg"></i><span>Claim My First Dibs Spot</span></>
          )}
        </button>
      </form>

      <p className="text-xs text-white/65 text-center mt-6 italic">
        No spam. No pressure. Just the cars you actually want, before anyone else sees them.
      </p>
    </div>
  );
});

NotificationOptIn.displayName = 'NotificationOptIn';

export default NotificationOptIn;
export { NotificationOptIn };
